import { z } from 'zod';
import type { BlockDefinition } from '../types';
import { sanitizedHtmlProps } from '../lib/sanitize';

export const bandStatsSchema = z.object({
  variant: z.enum(['ma', 'lime']).default('ma'),
  photo: z.object({
    src: z.string().min(1).max(1000),
    alt: z.string().max(200).optional(),
    tag: z.string().max(80).optional(),
    name: z.string().max(120).optional(),
  }),
  badge: z.string().max(120).optional(),
  eyebrow: z.string().max(160).optional(),
  quote: z.string().min(1).max(1000),
  author: z.object({
    initials: z.string().min(1).max(6),
    name: z.string().min(1).max(120),
    role: z.string().max(200),
  }),
  stats: z
    .array(
      z.object({
        value: z.string().min(1).max(20),
        suffix: z.string().max(20).optional(),
        label: z.string().min(1).max(120),
      }),
    )
    .min(1)
    .max(4),
});

export type BandStatsData = z.infer<typeof bandStatsSchema>;

export function BandStatsRenderer({
  variant = 'ma',
  photo,
  badge,
  eyebrow,
  quote,
  author,
  stats,
}: BandStatsData) {
  const safeStats = Array.isArray(stats) ? stats : [];
  return (
    <section className={`v5-band-${variant}`}>
      <div className={`v5-band-${variant}-orb v5-band-${variant}-orb-1`}></div>
      <div className={`v5-band-${variant}-orb v5-band-${variant}-orb-2`}></div>
      <div className={`v5-band-${variant}-noise`}></div>
      <div className={`container v5-band-${variant}-grid`}>
        <div className={`v5-band-${variant}-photo`}>
          <img src={photo.src} alt={photo.alt ?? ''} loading="lazy" />
          <div className={`v5-band-${variant}-photo-overlay`}></div>
          {badge ? (
            <div className={`v5-band-${variant}-badge`}>{badge}</div>
          ) : null}
          {(photo.tag || photo.name) ? (
            <div className={`v5-band-${variant}-photo-meta`}>
              {photo.tag ? <div className={`v5-band-${variant}-photo-tag`}>{photo.tag}</div> : null}
              {photo.name ? <div className={`v5-band-${variant}-photo-name`}>{photo.name}</div> : null}
            </div>
          ) : null}
        </div>
        <div className={`v5-band-${variant}-text`}>
          <div className={`v5-band-${variant}-quotemark`}>&ldquo;</div>
          {eyebrow ? (
            <div className={`v5-band-${variant}-eyebrow`}>
              <span className="dot"></span>
              {eyebrow}
            </div>
          ) : null}
          <blockquote dangerouslySetInnerHTML={sanitizedHtmlProps(quote)} />
          <div className={`v5-band-${variant}-author`}>
            <div className={`v5-band-${variant}-author-avatar`}>{author.initials}</div>
            <div className={`v5-band-${variant}-author-info`}>
              <strong>{author.name}</strong>
              <span>{author.role}</span>
            </div>
          </div>
          <div className={`v5-band-${variant}-stats`}>
            {safeStats.map((s, i) => (
              <div key={i}>
                <strong>
                  {s.value}
                  {s.suffix ? <small>{s.suffix}</small> : null}
                </strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export const bandStatsBlock: BlockDefinition<BandStatsData> = {
  meta: {
    type: 'band-stats',
    label: 'Banner testimonio + stats (v5)',
    description: 'Banner a sangre con foto a la izquierda y panel glass con cita + stats. Variantes MA (azul) y Lime.',
    category: 'social-proof',
    icon: 'quote',
  },
  schema: bandStatsSchema,
  defaults: {
    variant: 'ma',
    photo: {
      src: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=900&h=1100&fit=crop&q=85&auto=format',
      alt: 'Cliente verificado',
      tag: 'Caso real',
      name: 'JLS · Industrial Mediterráneo',
    },
    badge: '★★★★★ Reseña verificada',
    eyebrow: 'Caso · Venta por jubilación · Sector industrial',
    quote: 'Vender una empresa es complejo. Con <strong>este equipo</strong> fue totalmente <em>satisfactorio</em>. Serios, profesionales, te acompañan en todo y consiguen resultados.',
    author: {
      initials: 'JLS',
      name: 'José Luis Salazar',
      role: 'Fundador · vendió por jubilación · operación €8M+',
    },
    stats: [
      { value: '4,8', suffix: '/5', label: '★★★★★ +90 reseñas' },
      { value: '84', suffix: '%', label: 'Tasa de éxito' },
      { value: '9', suffix: ' meses', label: 'Operación media' },
    ],
  },
  fields: [
    {
      name: 'variant',
      label: 'Variante',
      type: 'select',
      options: [
        { label: 'M&A (azul oscuro)', value: 'ma' },
        { label: 'Lime (aceleradora)', value: 'lime' },
      ],
    },
    {
      name: 'photo',
      label: 'Foto',
      type: 'object',
      fields: [
        { name: 'src', label: 'URL imagen', type: 'image', required: true },
        { name: 'alt', label: 'Alt', type: 'text', max: 200 },
        { name: 'tag', label: 'Tag sobre foto', type: 'text', max: 80 },
        { name: 'name', label: 'Nombre sobre foto', type: 'text', max: 120 },
      ],
    },
    { name: 'badge', label: 'Badge (★★★★★)', type: 'text', max: 120 },
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 160 },
    { name: 'quote', label: 'Cita (HTML permitido)', type: 'richtext', required: true, max: 1000 },
    {
      name: 'author',
      label: 'Autor',
      type: 'object',
      fields: [
        { name: 'initials', label: 'Iniciales (avatar)', type: 'text', required: true, max: 6 },
        { name: 'name', label: 'Nombre', type: 'text', required: true, max: 120 },
        { name: 'role', label: 'Cargo / Contexto', type: 'text', max: 200 },
      ],
    },
    {
      name: 'stats',
      label: 'Stats',
      type: 'array',
      max: 4,
      itemFields: [
        { name: 'value', label: 'Valor', type: 'text', required: true, max: 20 },
        { name: 'suffix', label: 'Sufijo pequeño', type: 'text', max: 20 },
        { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 120 },
      ],
    },
  ],
  Renderer: BandStatsRenderer,
};
